import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import { apiFetch } from "../../api";

const OBTENIDO_LIST = [
  "Meta Ads",
  "Messenger",
  "Instagram",
  "Facebook",
  "Contacto Directo",
  "Otro",
];

const emptyClient = (telefono = "") => ({
  nombre: "",
  telefono,
  email: "",
  obtenido_de: "",
  observacion: "",
});

function normalizePhone(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 10);
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function statusLabel(estado) {
  if (estado === "ACTIVO") return "Activa";
  if (estado === "FUTURO") return "Futuro";
  if (estado === "VENTA") return "Venta";
  if (estado === "BAJA") return "Baja";
  return estado || "-";
}

function statusColor(estado) {
  if (estado === "ACTIVO") return "success";
  if (estado === "FUTURO") return "default";
  if (estado === "VENTA") return "success";
  if (estado === "BAJA") return "error";
  return "default";
}

function useVehicleOptions() {
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const controllerRef = React.useRef(null);

  const fetchOptions = React.useCallback(async (q) => {
    if (controllerRef.current) controllerRef.current.abort();
    const ctrl = new AbortController();
    controllerRef.current = ctrl;
    try {
      setLoading(true);
      const res = await apiFetch(`/api/vehiculos/?q=${encodeURIComponent(q || "")}`, {
        signal: ctrl.signal,
      });
      if (!res.ok) throw new Error("no response");
      const data = await res.json();
      setOptions(
        (Array.isArray(data) ? data : [])
          .map((x) => {
            if (typeof x === "string") return { id: null, label: x };
            return { id: x.id ?? null, label: x.modelo || x.nombre || x.name };
          })
          .filter((o) => o && o.label)
      );
    } catch (e) {
      if (e.name !== "AbortError") setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { options, fetchOptions, loading };
}

function consultaSubtitle(c) {
  if (c.estado === "FUTURO") {
    return c.fecha_programada ? `Para ${formatDate(c.fecha_programada)}` : "Sin fecha programada";
  }
  if (c.estado === "ACTIVO") return `Desde ${formatDate(c.fecha_inicio)}`;
  return `Cerró ${formatDate(c.fecha_cierre)}`;
}

export default function CrearClienteAdminDialog({ open, onClose, onCreated }) {
  const [step, setStep] = React.useState("telefono");
  const [telefono, setTelefono] = React.useState("");
  const [matches, setMatches] = React.useState([]);
  const [selectedClientId, setSelectedClientId] = React.useState(null);
  const [clientForm, setClientForm] = React.useState(emptyClient());
  const [consultaInput, setConsultaInput] = React.useState("");
  const [selectedVendedor, setSelectedVendedor] = React.useState(null);
  const [vendedores, setVendedores] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const vehicleApi = useVehicleOptions();

  const selectedClient = React.useMemo(
    () => matches.find((m) => String(m.id) === String(selectedClientId)) || null,
    [matches, selectedClientId]
  );
  const editingExisting = step !== "telefono" && step !== "nuevo" && matches.length > 0;

  React.useEffect(() => {
    if (!open) {
      setStep("telefono");
      setTelefono("");
      setMatches([]);
      setSelectedClientId(null);
      setClientForm(emptyClient());
      setConsultaInput("");
      setSelectedVendedor(null);
      setError(null);
      setLoading(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    apiFetch("/api/vendedores/listado?ocultar_inactivos=true&limit=1000", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        setVendedores(items.filter((v) => v.activo !== false));
      })
      .catch(() => {
        if (!cancelled) setVendedores([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const emitCreated = (cliente = null, consulta = null) => {
    onCreated?.({ cliente, consulta });
    window.dispatchEvent(new CustomEvent("admin_cliente_changed", { detail: { cliente, consulta } }));
  };

  const searchPhone = async () => {
    const digits = normalizePhone(telefono);
    if (!digits) {
      setError("Ingresá un teléfono.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/clientes/buscar-telefono?telefono=${encodeURIComponent(digits)}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const found = Array.isArray(data.matches) ? data.matches : [];
      setMatches(found);
      if (found.length === 0) {
        setClientForm(emptyClient(digits));
        setStep("nuevo");
      } else if (found.length === 1) {
        setSelectedClientId(found[0].id);
        setClientForm({ ...emptyClient(digits), ...found[0], telefono: found[0].telefono || digits });
        setStep("cliente");
      } else {
        setStep("elegir");
      }
    } catch (e) {
      setError("No se pudo buscar el teléfono.");
    } finally {
      setLoading(false);
    }
  };

  const selectClient = (client) => {
    setSelectedClientId(client.id);
    setClientForm({ ...emptyClient(telefono), ...client });
  };

  const clientPayload = () => ({
    nombre: clientForm.nombre.trim(),
    telefono: normalizePhone(clientForm.telefono || telefono) || null,
    email: clientForm.email?.trim() || null,
    obtenido_de: clientForm.obtenido_de?.trim() || null,
    observacion: clientForm.observacion?.trim() || null,
  });

  const validateClient = () => {
    if (!clientForm.nombre?.trim()) {
      setError("El nombre del cliente es obligatorio.");
      return false;
    }
    const email = clientForm.email?.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Ingresá un email válido.");
      return false;
    }
    return true;
  };

  const createClientOnly = async () => {
    if (!validateClient()) return null;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch("/api/clientes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...clientPayload(), consultas: [] }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "No se pudo crear el cliente.");
      }
      const created = await res.json();
      emitCreated(created, null);
      onClose?.();
      return created;
    } catch (e) {
      setError(e.message || "No se pudo crear el cliente.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const saveExistingClient = async ({ close = true } = {}) => {
    if (!selectedClient || !validateClient()) return null;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/clientes/${selectedClient.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(clientPayload()),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "No se pudo guardar el cliente.");
      }
      const updated = await res.json();
      setMatches((prev) => prev.map((m) => (String(m.id) === String(updated.id) ? updated : m)));
      setClientForm(updated);
      emitCreated(updated, null);
      if (close) onClose?.();
      return updated;
    } catch (e) {
      setError(e.message || "No se pudo guardar el cliente.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const goToConsulta = async () => {
    if (step === "nuevo") {
      if (!validateClient()) return;
      setError(null);
      setStep("consulta");
      return;
    }
    const updated = await saveExistingClient({ close: false });
    if (updated) setStep("consulta");
  };

  const createConsultaForClient = async (cliente) => {
    const vehiculo = consultaInput.trim();
    if (!vehiculo) {
      setError("El vehículo consultado es obligatorio.");
      return null;
    }
    const activeSameVehicle = (cliente?.consultas || []).find(
      (c) => c.estado === "ACTIVO" && String(c.vehiculo_consultado || "").trim().toLowerCase() === vehiculo.toLowerCase()
    );
    if (activeSameVehicle) {
      const ok = window.confirm("Hay una consulta activa con el mismo vehículo. ¿Deseás crear otra consulta igualmente?");
      if (!ok) return null;
    }

    const res = await apiFetch(`/api/clientes/${cliente.id}/consultas`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        vehiculo_consultado: vehiculo,
        vendedor_id: selectedVendedor?.id ?? null,
      }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || "No se pudo crear la consulta.");
    }
    return res.json();
  };

  const confirmConsulta = async () => {
    if (!consultaInput.trim()) {
      setError("El vehículo consultado es obligatorio.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let cliente = selectedClient;
      if (step === "consulta" && !selectedClient) {
        if (!validateClient()) return;
        const res = await apiFetch("/api/clientes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ ...clientPayload(), consultas: [] }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || "No se pudo crear el cliente.");
        }
        cliente = await res.json();
      }

      const consulta = await createConsultaForClient(cliente);
      if (!consulta) return;
      emitCreated(cliente, consulta);
      onClose?.();
    } catch (e) {
      setError(e.message || "No se pudo crear la consulta.");
    } finally {
      setLoading(false);
    }
  };

  const renderClientFields = () => (
    <Stack spacing={1.5}>
      <TextField label="Nombre" value={clientForm.nombre || ""} onChange={(e) => setClientForm((p) => ({ ...p, nombre: e.target.value }))} required />
      <TextField label="Teléfono" value={clientForm.telefono || ""} disabled inputProps={{ inputMode: "numeric" }} />
      <TextField label="Email" type="email" value={clientForm.email || ""} onChange={(e) => setClientForm((p) => ({ ...p, email: e.target.value }))} />
      <Autocomplete
        freeSolo
        options={OBTENIDO_LIST}
        value={clientForm.obtenido_de || null}
        onInputChange={(e, value) => setClientForm((p) => ({ ...p, obtenido_de: value }))}
        onChange={(e, value) => setClientForm((p) => ({ ...p, obtenido_de: value || "" }))}
        renderInput={(params) => <TextField {...params} label="Origen" />}
      />
      <TextField label="Observación" value={clientForm.observacion || ""} onChange={(e) => setClientForm((p) => ({ ...p, observacion: e.target.value }))} multiline minRows={3} />
    </Stack>
  );

  const consultas = selectedClient?.consultas || [];

  const renderConsultaList = () => {
    if (consultas.length === 0) {
      return <Box sx={{ border: "1px dashed #c9d7ea", borderRadius: "8px", p: 2, color: "#4c5d86" }}>Este cliente no tiene consultas previas.</Box>;
    }
    return (
      <Stack spacing={1}>
        {consultas.map((c) => (
          <Box key={c.id} sx={{ border: "1px solid #e4ebf5", borderRadius: "8px", p: 1.25 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 900 }}>{c.vehiculo_consultado || "Sin vehículo"}</Typography>
                <Typography sx={{ fontSize: 12, color: "#4c5d86" }}>
                  {c.vendedor_nombre || "Sin asignar"} · {consultaSubtitle(c)}
                </Typography>
              </Box>
              <Chip size="small" label={statusLabel(c.estado)} color={statusColor(c.estado)} />
            </Box>
          </Box>
        ))}
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 24, fontWeight: 900 }}>Crear cliente</Typography>
            {editingExisting && (
              <Typography sx={{ fontSize: 12, color: "#7b8798", fontWeight: 700 }}>
                Se encontró cliente existente
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} disabled={loading}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      {loading && <LinearProgress />}
      <DialogContent sx={{ px: 3, pt: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === "telefono" && (
          <Stack spacing={2}>
            <Typography sx={{ color: "#4c5d86" }}>Ingresá el teléfono del cliente.</Typography>
            <TextField
              autoFocus
              label="Teléfono"
              value={telefono}
              onChange={(e) => setTelefono(normalizePhone(e.target.value))}
              inputProps={{ inputMode: "numeric" }}
              InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1, color: "#4c5d86" }} /> }}
              onKeyDown={(e) => { if (e.key === "Enter") searchPhone(); }}
            />
          </Stack>
        )}

        {step === "elegir" && (
          <Stack spacing={2}>
            <Typography sx={{ fontWeight: 800 }}>Encontré más de un cliente con ese teléfono. Elegí cuál corresponde.</Typography>
            <List sx={{ border: "1px solid #e4ebf5", borderRadius: "8px" }}>
              {matches.map((client) => (
                <ListItemButton key={client.id} selected={String(selectedClientId) === String(client.id)} onClick={() => selectClient(client)}>
                  <PersonIcon sx={{ mr: 1, color: "#0059f6" }} />
                  <Box>
                    <Typography sx={{ fontWeight: 900 }}>{client.nombre}</Typography>
                    <Typography sx={{ fontSize: 12, color: "#4c5d86" }}>{client.telefono} · {(client.consultas || []).length} consultas</Typography>
                  </Box>
                </ListItemButton>
              ))}
            </List>
          </Stack>
        )}

        {(step === "cliente" || step === "nuevo") && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Información del cliente</Typography>
              {renderClientFields()}
            </Box>
            <Box sx={{ border: "1px solid #e4ebf5", borderRadius: "8px", p: 2, alignSelf: "start" }}>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Consultas</Typography>
              {step === "nuevo" ? (
                <Typography sx={{ color: "#4c5d86", fontSize: 13 }}>
                  Podés crear el cliente sin consultas o agregar una consulta ahora.
                </Typography>
              ) : renderConsultaList()}
            </Box>
          </Box>
        )}

        {step === "consulta" && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Consultas existentes</Typography>
              {selectedClient ? renderConsultaList() : (
                <Box sx={{ border: "1px dashed #c9d7ea", borderRadius: "8px", p: 2, color: "#4c5d86" }}>
                  Este cliente se creará junto con la consulta.
                </Box>
              )}
            </Box>
            <Stack spacing={1.5}>
              <Typography sx={{ fontWeight: 900 }}>Nueva consulta</Typography>
              <Autocomplete
                freeSolo
                options={vehicleApi.options}
                getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.label || "")}
                inputValue={consultaInput}
                onInputChange={(e, value, reason) => {
                  setConsultaInput(value);
                  if (reason === "input") vehicleApi.fetchOptions(value);
                }}
                onOpen={() => vehicleApi.fetchOptions("")}
                onChange={(e, value) => setConsultaInput(typeof value === "string" ? value : value?.label || "")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vehículo consultado"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {vehicleApi.loading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Autocomplete
                options={vendedores}
                value={selectedVendedor}
                onChange={(e, value) => setSelectedVendedor(value)}
                getOptionLabel={(opt) => opt?.nombre || ""}
                isOptionEqualToValue={(opt, value) => String(opt.id) === String(value.id)}
                renderInput={(params) => <TextField {...params} label="Vendedor (opcional)" placeholder="Sin asignar" />}
              />
              <Alert severity="info">
                Si no elegís vendedor, la consulta quedará en Consultas no asignadas.
              </Alert>
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {step !== "telefono" && (
          <Button disabled={loading} onClick={() => {
            if (step === "elegir" || step === "nuevo") setStep("telefono");
            else if (step === "cliente") setStep(matches.length > 1 ? "elegir" : "telefono");
            else if (step === "consulta") setStep(selectedClient ? "cliente" : "nuevo");
          }}>
            Anterior
          </Button>
        )}
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        {step === "telefono" && <Button variant="contained" onClick={searchPhone} disabled={loading}>Continuar</Button>}
        {step === "elegir" && <Button variant="contained" onClick={() => selectedClientId && setStep("cliente")} disabled={!selectedClientId || loading}>Continuar</Button>}
        {step === "nuevo" && (
          <>
            <Button variant="outlined" onClick={goToConsulta} disabled={loading}>Agregar consulta</Button>
            <Button variant="contained" onClick={createClientOnly} disabled={loading}>Crear cliente</Button>
          </>
        )}
        {step === "cliente" && (
          <>
            <Button variant="outlined" onClick={goToConsulta} disabled={loading}>Agregar consulta</Button>
            <Button variant="contained" onClick={() => saveExistingClient({ close: true })} disabled={loading}>Guardar cliente</Button>
          </>
        )}
        {step === "consulta" && (
          <Button variant="contained" onClick={confirmConsulta} disabled={loading || !consultaInput.trim()}>
            Crear consulta
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
